import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Flame, Waves, Crown, Zap, Gem, Heart, Radio, Accessibility } from "lucide-react";
import { formatMoney } from "@/lib/money";
import { useMotionPreference } from "@/hooks/useMotionPreference";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuRadioGroup, DropdownMenuRadioItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Switch } from "@/components/ui/switch";

type Reaction = {
  id: string;
  creator_id: string;
  supporter_id: string;
  tier: string;
  animation_key: string;
  amount_cents: number;
  message: string | null;
  created_at: string;
  supporter_name?: string;
  supporter_cashtag?: string;
};

type TierMeta = {
  icon: any;
  label: string;
  color: string;     // text color class
  hex: string;       // raw color for box-shadow / spark
  tint: string;      // bg tint for row flash
  ring: string;
  haptic: number[];  // navigator.vibrate pattern
  sparks: number;    // number of spark particles
  emoji: string;
};

const TIER_META: Record<string, TierMeta> = {
  micro:   { icon: Heart, label: "Micro Tip",    color: "text-pink-500",   hex: "rgb(236 72 153)",  tint: "rgba(236,72,153,0.15)",  ring: "ring-pink-500/40",   haptic: [20],                 sparks: 4,  emoji: "💗" },
  fire:    { icon: Flame, label: "Fire Drop",    color: "text-orange-500", hex: "rgb(249 115 22)",  tint: "rgba(249,115,22,0.18)",  ring: "ring-orange-500/40", haptic: [30, 40, 30],         sparks: 8,  emoji: "🔥" },
  wave:    { icon: Waves, label: "Wave Boost",   color: "text-sky-500",    hex: "rgb(14 165 233)",  tint: "rgba(14,165,233,0.18)",  ring: "ring-sky-500/40",    haptic: [40, 60, 40],         sparks: 8,  emoji: "🌊" },
  crown:   { icon: Crown, label: "Crown Drop",   color: "text-amber-500",  hex: "rgb(245 158 11)",  tint: "rgba(245,158,11,0.22)",  ring: "ring-amber-500/40",  haptic: [60, 40, 60, 40, 80], sparks: 12, emoji: "👑" },
  surge:   { icon: Zap,   label: "Surge",        color: "text-violet-500", hex: "rgb(139 92 246)",  tint: "rgba(139,92,246,0.20)",  ring: "ring-violet-500/40", haptic: [80, 30, 80],         sparks: 10, emoji: "⚡" },
  diamond: { icon: Gem,   label: "Diamond Drop", color: "text-cyan-500",   hex: "rgb(6 182 212)",   tint: "rgba(6,182,212,0.22)",   ring: "ring-cyan-500/40",   haptic: [100, 40, 100, 40, 120], sparks: 16, emoji: "💎" },
};

const tierFor = (r: Reaction): TierMeta => TIER_META[r.tier] ?? TIER_META.micro;

const timeAgo = (iso: string) => {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 5) return "just now";
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

type Burst = { id: string; meta: TierMeta };

const HAPTIC_KEY = "ci.hapticEnabled";

export const LiveReactionsFeed = ({ creatorId }: { creatorId: string }) => {
  const [items, setItems] = useState<Reaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [pulseId, setPulseId] = useState<string | null>(null);
  const [bursts, setBursts] = useState<Burst[]>([]);
  const [announcement, setAnnouncement] = useState("");
  const [hapticsEnabled, setHapticsEnabled] = useState<boolean>(() => {
    if (typeof window === "undefined") return true;
    return localStorage.getItem(HAPTIC_KEY) !== "0";
  });
  const { pref: motionPref, setPref: setMotionPref, reduced } = useMotionPreference();
  const profileCache = useRef<Map<string, { display_name: string; cashtag: string }>>(new Map());
  const initialLoadDone = useRef(false);

  const toggleHaptics = (on: boolean) => {
    setHapticsEnabled(on);
    localStorage.setItem(HAPTIC_KEY, on ? "1" : "0");
  };

  const enrich = async (rows: Reaction[]): Promise<Reaction[]> => {
    const missing = Array.from(
      new Set(rows.map((r) => r.supporter_id).filter((id) => !profileCache.current.has(id))),
    );
    if (missing.length) {
      const { data } = await supabase
        .from("public_profiles")
        .select("user_id, display_name, cashtag")
        .in("user_id", missing);
      (data ?? []).forEach((p: any) =>
        profileCache.current.set(p.user_id, { display_name: p.display_name, cashtag: p.cashtag }),
      );
    }
    return rows.map((r) => {
      const p = profileCache.current.get(r.supporter_id);
      return { ...r, supporter_name: p?.display_name ?? "Supporter", supporter_cashtag: p?.cashtag };
    });
  };

  const triggerBurst = (meta: TierMeta, supporter: string) => {
    // Always announce to screen readers — independent of motion/haptics.
    setAnnouncement(`${supporter} sent a ${meta.label}`);

    if (reduced) return; // skip visual burst + haptics in reduced-motion mode

    const id = crypto.randomUUID();
    setBursts((b) => [...b, { id, meta }]);
    setTimeout(() => setBursts((b) => b.filter((x) => x.id !== id)), 1500);

    if (hapticsEnabled && typeof navigator !== "undefined" && "vibrate" in navigator) {
      try { navigator.vibrate(meta.haptic); } catch { /* noop */ }
    }
  };

  useEffect(() => {
    if (!creatorId) return;
    let active = true;

    (async () => {
      const { data } = await supabase
        .from("live_reactions")
        .select("id, creator_id, supporter_id, tier, animation_key, amount_cents, message, created_at")
        .eq("creator_id", creatorId)
        .order("created_at", { ascending: false })
        .limit(20);
      if (!active) return;
      const enriched = await enrich((data ?? []) as Reaction[]);
      if (!active) return;
      setItems(enriched);
      setLoading(false);
      initialLoadDone.current = true;
    })();

    const channel = supabase
      .channel(`live-reactions:${creatorId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "live_reactions",
          filter: `creator_id=eq.${creatorId}`,
        },
        async (payload) => {
          const row = payload.new as Reaction;
          const [enriched] = await enrich([row]);
          if (!active) return;
          const meta = tierFor(enriched);
          setItems((prev) => [enriched, ...prev].slice(0, 20));
          setPulseId(enriched.id);
          setTimeout(() => setPulseId(null), 1400);
          if (initialLoadDone.current) {
            const supporter = enriched.supporter_cashtag
              ? `$${enriched.supporter_cashtag}`
              : enriched.supporter_name ?? "A supporter";
            triggerBurst(meta, supporter);
          }
        },
      )
      .subscribe();

    return () => {
      active = false;
      supabase.removeChannel(channel);
    };
  }, [creatorId]);

  return (
    <div
      className={`rounded-2xl bg-background shadow-card overflow-hidden relative ${
        reduced ? "motion-reduced" : ""
      }`}
    >
      {/* Screen-reader announcer */}
      <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
        {announcement}
      </div>

      {/* Burst overlay — hidden from AT and skipped entirely in reduced-motion mode */}
      {!reduced && (
        <div className="pointer-events-none absolute inset-0 z-10 overflow-hidden" aria-hidden="true">
          {bursts.map((b) => {
            const Icon = b.meta.icon;
            return (
              <div key={b.id} className="absolute left-1/2 bottom-12">
                <div
                  className="reaction-burst"
                  style={{ filter: `drop-shadow(0 0 12px ${b.meta.hex})` }}
                >
                  <div className={`text-4xl ${b.meta.color}`}>
                    <Icon className="h-10 w-10" />
                  </div>
                </div>
                {Array.from({ length: b.meta.sparks }).map((_, i) => {
                  const angle = (Math.PI * 2 * i) / b.meta.sparks;
                  const dist = 60 + Math.random() * 40;
                  return (
                    <span
                      key={i}
                      className="reaction-spark absolute left-1/2 top-1/2 block w-1.5 h-1.5 rounded-full"
                      style={{
                        background: b.meta.hex,
                        ["--dx" as any]: `${Math.cos(angle) * dist}px`,
                        ["--dy" as any]: `${Math.sin(angle) * dist}px`,
                      }}
                    />
                  );
                })}
              </div>
            );
          })}
        </div>
      )}

      <div className="flex items-center justify-between px-4 py-3 border-b border-border/60">
        <div className="flex items-center gap-2">
          <span className="relative flex h-2 w-2" aria-hidden="true">
            <span className="absolute inline-flex h-full w-full rounded-full bg-rose-500 opacity-75 animate-ping" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-rose-500" />
          </span>
          <p className="font-semibold text-sm">Live Reactions</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground flex items-center gap-1">
            <Radio className="h-3 w-3" aria-hidden="true" /> realtime
          </span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label="Accessibility & motion options"
                className="h-8 w-8 grid place-items-center rounded-full hover:bg-muted transition-smooth"
              >
                <Accessibility className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60">
              <DropdownMenuLabel>Motion</DropdownMenuLabel>
              <DropdownMenuRadioGroup value={motionPref} onValueChange={(v) => setMotionPref(v as any)}>
                <DropdownMenuRadioItem value="auto">
                  Match system{reduced && motionPref === "auto" ? " (reduced)" : ""}
                </DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="full">Full animations</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="reduced">Reduced motion</DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
              <DropdownMenuSeparator />
              <DropdownMenuLabel>Haptics</DropdownMenuLabel>
              <DropdownMenuItem
                onSelect={(e) => e.preventDefault()}
                className="flex items-center justify-between"
              >
                <span className="text-sm">Vibrate on new reaction</span>
                <Switch
                  checked={hapticsEnabled && !reduced}
                  disabled={reduced}
                  onCheckedChange={toggleHaptics}
                  aria-label="Toggle haptic feedback"
                />
              </DropdownMenuItem>
              {reduced && (
                <p className="px-2 pb-2 text-[10px] text-muted-foreground">
                  Haptics paused while reduced motion is on.
                </p>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>


      {loading ? (
        <div className="px-4 py-8 text-center text-xs text-muted-foreground">Tuning in…</div>
      ) : items.length === 0 ? (
        <div className="px-4 py-8 text-center text-xs text-muted-foreground">
          No reactions yet. When fans send support, drops will appear here in real time.
        </div>
      ) : (
        <ul className="divide-y divide-border/60 max-h-80 overflow-y-auto">
          {items.map((r) => {
            const meta = tierFor(r);
            const Icon = meta.icon;
            const isPulse = pulseId === r.id;
            return (
              <li
                key={r.id}
                className={`flex items-center gap-3 px-4 py-3 ${isPulse ? "reaction-row" : ""}`}
                style={isPulse ? ({ ["--reaction-tint" as any]: meta.tint } as any) : undefined}
              >
                <div
                  className={`relative h-9 w-9 rounded-full bg-muted grid place-items-center ${meta.color} ${
                    isPulse ? "reaction-shake reaction-glow" : ""
                  }`}
                  style={
                    isPulse
                      ? ({ ["--reaction-color" as any]: meta.hex } as any)
                      : undefined
                  }
                >
                  <Icon className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">
                    {r.supporter_cashtag ? `$${r.supporter_cashtag}` : r.supporter_name}{" "}
                    <span className="text-muted-foreground font-normal">
                      sent a {meta.label} {meta.emoji}
                    </span>
                  </p>
                  {r.message && (
                    <p className="text-xs text-muted-foreground truncate">"{r.message}"</p>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <p className="text-sm font-semibold">{formatMoney(r.amount_cents)}</p>
                  <p className="text-[10px] text-muted-foreground">{timeAgo(r.created_at)}</p>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
